/**
 * The sourcing vocabulary — channels and run statuses — and the words and badges for
 * each. The sourcing analogue of `web/src/recruiting/outreach.ts`, and it exists for
 * the same reason: the compliance facts belong in one table a reader can audit, not
 * scattered across the screen that renders them.
 *
 * ── The compliance reality this table encodes ────────────────────────────────
 *
 * `src/modules/sourcing/adapter.ts`'s own words: nine channels, a closed set, four
 * low-risk and shipped in every build's default registry, five high-risk and
 * QUARANTINED — they never appear in any registry unless an operator-level
 * environment flag is set, and even then still need a per-company policy row. This
 * screen goes further than the server does: it never renders the five as a
 * selectable option at all, regardless of what any flag or policy row says — see
 * `web/shapes/sourcing.md` §1. `LOW_RISK_SOURCING_CHANNELS` is exactly the closed set
 * the trigger form's `<select>` may ever hold a value from.
 *
 * Transcribed rather than imported from `src/modules/sourcing/*`, for the reason
 * `outreach.ts` gives: the SPA bundle is served to browsers, and a `web/ →
 * src/modules/` import would drag a `pg` handle into one.
 */

import type { MessageKey } from '../i18n/messages';
import type { IconName, Severity } from '../states/icons';
import type { SourcingChannel, SourcingRunStatus } from '../api/sourcing';

export { SOURCING_CHANNELS, SOURCING_RUN_STATUSES } from '../api/sourcing';
export type { SourcingChannel, SourcingRunStatus } from '../api/sourcing';

/** The four channels usable by default, and the only values the trigger `<select>` offers. */
export const LOW_RISK_SOURCING_CHANNELS: readonly SourcingChannel[] = [
  'career_page',
  'ats_import',
  'email',
  'job_board_api',
];

/** The five quarantined channels — never selectable here, always shown with the warning. */
export const HIGH_RISK_SOURCING_CHANNELS: readonly SourcingChannel[] = [
  'linkedin_dm',
  'linkedin_comment',
  'facebook_dm',
  'facebook_comment',
  'web_scrape',
];

export function isHighRiskSourcingChannel(channel: SourcingChannel): boolean {
  return !LOW_RISK_SOURCING_CHANNELS.includes(channel);
}

/** The name of a channel, for the run list, the trigger `<select>` and the quarantine rows. */
export const SOURCING_CHANNEL_LABEL: Readonly<Record<SourcingChannel, MessageKey>> = {
  career_page: 'sourcing.channel.career_page',
  ats_import: 'sourcing.channel.ats_import',
  email: 'sourcing.channel.email',
  job_board_api: 'sourcing.channel.job_board_api',
  linkedin_dm: 'sourcing.channel.linkedin_dm',
  linkedin_comment: 'sourcing.channel.linkedin_comment',
  facebook_dm: 'sourcing.channel.facebook_dm',
  facebook_comment: 'sourcing.channel.facebook_comment',
  web_scrape: 'sourcing.channel.web_scrape',
};

/** The name of a run status, for the run list's badge. */
export const SOURCING_RUN_STATUS_LABEL: Readonly<Record<SourcingRunStatus, MessageKey>> = {
  pending: 'sourcing.run.status.pending',
  running: 'sourcing.run.status.running',
  completed: 'sourcing.run.status.completed',
  failed: 'sourcing.run.status.failed',
};

/**
 * The badge severity a run status is drawn at. `pending`/`running` are in-flight
 * (`info`); `completed` is the good terminal (`low`); `failed` is worth noticing
 * (`high`). Icon and text carry the meaning; colour is the third channel
 * (`DESIGN.md` §4).
 */
export const SOURCING_RUN_STATUS_BADGE: Readonly<Record<SourcingRunStatus, Severity>> = {
  pending: 'info',
  running: 'info',
  completed: 'low',
  failed: 'high',
};

/**
 * The icon a run status is drawn with. `hourglass` for a run not yet picked up,
 * `refresh` for one in flight, `circle-check`/`triangle-alert` for the two terminals
 * — four distinct silhouettes, never colour alone (`DESIGN.md` §4).
 */
export const SOURCING_RUN_STATUS_ICON: Readonly<Record<SourcingRunStatus, IconName>> = {
  pending: 'hourglass',
  running: 'refresh',
  completed: 'circle-check',
  failed: 'triangle-alert',
};
